(() => {
  'use strict';
  if (customElements.get('sf-klarna-financing')) return;
  const sdkUrl = 'https://js.klarna.com/web-sdk/v1/klarna.js';
  // Klarna braucht auf langsamen Verbindungen deutlich laenger als 12 s; erst
  // danach gilt die Rate als nicht verfuegbar und die Zeile verschwindet still.
  const SDK_TIMEOUT = 30000;
  const RENDER_TIMEOUT = 15000;
  let sdkPromise;
  let sdkIdentity;

  function loadSdk(clientId, environment) {
    const identity = `${environment}:${clientId}`;
    if (sdkIdentity && sdkIdentity !== identity) return Promise.reject(new Error('Klarna configuration conflict'));
    if (sdkPromise) return sdkPromise;
    sdkIdentity = identity;
    sdkPromise = new Promise((resolve, reject) => {
      let script = document.querySelector(`script[src="${sdkUrl}"]`);
      if (script && (script.dataset.clientId !== clientId || script.dataset.environment !== environment)) {
        reject(new Error('Klarna configuration conflict'));
        return;
      }
      if (script && window.Klarna?.OnsiteMessaging) return resolve();
      const isNew = !script;
      if (isNew) {
        script = document.createElement('script');
        script.src = sdkUrl;
        script.async = true;
        script.dataset.clientId = clientId;
        script.dataset.environment = environment;
      }
      const timer = setTimeout(() => reject(new Error('Klarna unavailable')), SDK_TIMEOUT);
      script.addEventListener('load', () => { clearTimeout(timer); resolve(); }, { once: true });
      script.addEventListener('error', () => {
        clearTimeout(timer);
        if (isNew) script.remove();
        reject(new Error('Klarna unavailable'));
      }, { once: true });
      if (isNew) document.head.appendChild(script);
    }).then(() => {
      // Klarna zeichnet alle Platzhalter der Seite; auch Karten in einem
      // Karussell ausserhalb des Sichtfelds sollen ihren Zustand kennen.
      window.dispatchEvent(new CustomEvent('sf:klarna-sdk-ready'));
    }).catch(error => { sdkPromise = undefined; sdkIdentity = undefined; throw error; });
    return sdkPromise;
  }

  // Ein gemeinsamer, entprellter Refresh: mehrere Kaufbereiche einer Seite
  // loesen sonst parallele Neuzeichnungen aus, die sich gegenseitig abbrechen.
  let refreshTimer;
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => window.Klarna?.OnsiteMessaging?.refresh(), 120);
  }

  // Klarna ist hier ein Zahlungsdienst, kein Statistik- oder Marketing-
  // Werkzeug: die Rate laedt unabhaengig von der Cookie-Auswahl (Nayl,
  // 17.10.2026: "sofort die Rate darunter"). Hinweis gehoert in die
  // Datenschutzerklaerung.
  let noteId = 0;
  const observer = typeof window.IntersectionObserver === 'function'
    ? new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        entry.target.start();
      }
    }, { rootMargin: '600px 0px' })
    : null;

  class Financing extends HTMLElement {
    static get observedAttributes() { return ['data-amount', 'data-ready']; }
    connectedCallback() {
      this.placement = this.querySelector('klarna-placement');
      this.totalValue = this.querySelector('[data-sf-klarna-total-value]');
      this.english = this.dataset.locale === 'en-DE';
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      try { this.variants = JSON.parse(this.querySelector('[data-sf-klarna-variants]')?.textContent || '{}'); }
      catch { this.variants = {}; }
      // "Raten & Details" klappt eine kurze Erklaerung unter der Zeile auf.
      const more = this.querySelector('[data-sf-klarna-more]');
      const note = this.querySelector('[data-sf-klarna-note]');
      if (more && note) {
        if (!note.id) note.id = `sf-financing-note-${++noteId}`;
        more.setAttribute('aria-controls', note.id);
        more.addEventListener('click', () => {
          const open = more.getAttribute('aria-expanded') !== 'true';
          more.setAttribute('aria-expanded', String(open));
          note.hidden = !open;
        }, options);
      }
      document.addEventListener('variant:change', event => this.variantChanged(event), { ...options, capture: true });
      document.addEventListener('change', event => this.variantChanged(event), options);
      document.addEventListener('cart:updated', event => this.cartChanged(event), options);
      // Set-Picker (product) und Bundle-Module (bundle) melden ihre eigene
      // Auswahlsumme; der Scope ist immer die mitgelieferte Form-/Kartenkennung.
      window.addEventListener('sf:set-total', event => {
        if (event.detail?.form !== this.dataset.form || this.dataset.context === 'cart') return;
        this.composed = true;
        this.dataset.compare = String(Number(event.detail.compare) || 0);
        this.dataset.ready = String(Boolean(event.detail.ready));
        this.dataset.amount = String(event.detail.total);
      }, options);
      window.addEventListener('sf:klarna-sdk-ready', () => this.start(), options);
      this.sync();
      if (this.started) {
        // Verschoben statt neu (Karussell, Neuordnung): die Beobachtung lief
        // beim Abhaengen aus, also erneut auf Klarnas Antwort warten.
        if (window.Klarna?.OnsiteMessaging) { scheduleRefresh(); this.watchRender(); }
        return;
      }
      // Ist Klarna schon geladen (Warenkorb-Neuaufbau, Quick-View), sofort.
      if (window.Klarna?.OnsiteMessaging || !observer) this.start();
      else observer.observe(this);
    }
    disconnectedCallback() {
      this.abort?.abort();
      observer?.unobserve(this);
      clearInterval(this.renderTimer);
    }
    attributeChangedCallback() { if (this.isConnected && this.placement) this.sync(); }
    money(amount) {
      return new Intl.NumberFormat(this.english ? 'en-DE' : 'de-DE', { style: 'currency', currency: 'EUR' }).format(amount / 100);
    }
    sync() {
      const amount = Number(this.dataset.amount);
      const ready = this.dataset.ready === 'true' && Number.isSafeInteger(amount) && amount > 0;
      const changed = this.placement.dataset.purchaseAmount !== String(amount);
      this.hidden = !ready || (this.dataset.state === 'unavailable' && !changed);
      if (!ready) return;
      this.placement.dataset.purchaseAmount = String(amount);
      // Neben der Monatsrate steht immer der Kaufpreis, auf den sie sich bezieht.
      const money = this.money(amount);
      if (this.totalValue) this.totalValue.textContent = money;
      // A composed set must show the same purchase total above and inside the offer.
      if (this.dataset.context === 'product' && (this.composed || this.dataset.composed === 'true')) {
        const nativePrice = this.closest('[id^="ProductInfo-"]')?.querySelector('[id^="Price-"]');
        const regular = nativePrice?.querySelector('.price__regular');
        if (regular) {
          regular.textContent = money;
          // Streichpreis bleibt, solange der Set-Picker einen hoeheren
          // Vergleichspreis meldet (Aktionen per compare_at_price).
          const compare = Number(this.dataset.compare) || 0;
          const onSale = compare > amount;
          const sale = nativePrice.querySelector('.price__sale');
          nativePrice.querySelector('.price')?.classList.toggle('price--on-sale', onSale && Boolean(sale));
          nativePrice.querySelectorAll('.price__sale, .unit-price, .sr-only').forEach(label => { label.hidden = true; });
          if (onSale && sale) {
            sale.textContent = this.money(compare);
            sale.hidden = false;
          }
        }
      }
      if (this.started && changed && window.Klarna?.OnsiteMessaging) {
        // Neuer Betrag, neue Rate: ein Betrag ohne Klarna-Angebot darf eine
        // spaetere gueltige Auswahl nicht dauerhaft ausblenden.
        if (this.dataset.state === 'unavailable') { this.dataset.state = 'loading'; this.hidden = false; }
        scheduleRefresh();
        this.watchRender(400);
      }
      // Erst jetzt vollstaendig ausgewaehlt und Klarna laeuft bereits: direkt.
      if (!this.started && window.Klarna?.OnsiteMessaging) this.start();
    }
    variantChanged(event) {
      if (this.dataset.context !== 'product' || this.composed || this.dataset.composed === 'true') return;
      const form = document.getElementById(this.dataset.form);
      if (!form || !(form.contains(event.target) || event.target?.getAttribute?.('form') === this.dataset.form)) return;
      const variant = event.detail?.variant || this.variants[form.querySelector('[name="id"]')?.value];
      const quantity = Number(form.querySelector('[name="quantity"]')?.value || document.querySelector(`[name="quantity"][form="${CSS.escape(this.dataset.form)}"]`)?.value || 1);
      this.dataset.ready = String(Boolean(variant?.available) && !form.querySelector('[name="selling_plan"]')?.value);
      this.dataset.amount = String(Number(variant?.price) * quantity);
    }
    cartChanged(event) {
      if (this.dataset.context !== 'cart' || !event.detail?.cart) return;
      const cart = event.detail.cart;
      const excluded = !cart.items?.length || cart.items.some(item => item.selling_plan_allocation || item.properties?.Vorbestellung);
      this.dataset.ready = String(!excluded);
      this.dataset.amount = String(cart.total_price);
    }
    async start() {
      if (this.started || this.hidden || !this.dataset.clientId || !this.isConnected) return;
      this.started = true;
      if (this.dataset.state !== 'ready') this.dataset.state = 'loading';
      try {
        await loadSdk(this.dataset.clientId, this.dataset.environment);
        if (!this.isConnected) return;
        // Neu eingefuegte Platzhalter (Warenkorb-Neuaufbau, Quick-View)
        // zeichnet Klarna erst nach einem Refresh.
        scheduleRefresh();
        this.watchRender();
      } catch { if (this.isConnected) this.unavailable(); }
    }
    watchRender(settle = 0) {
      clearInterval(this.renderTimer);
      const startedAt = Date.now();
      this.renderTimer = setInterval(() => {
        if (!this.isConnected) return clearInterval(this.renderTimer);
        // Nach einem Betragswechsel kurz warten, sonst zaehlt die alte Rate.
        if (Date.now() - startedAt < settle) return;
        const message = this.placement.shadowRoot?.querySelector('[part~="osm-message"]')?.textContent?.trim();
        if (message) {
          clearInterval(this.renderTimer);
          // Kleine Betraege bekommen von Klarna "In 30 Tagen bezahlen" statt
          // einer Rate; dann passt die Raten-Zeile darunter nicht.
          this.dataset.offer = /\/\s*(Monat|month)|Rate|instal/i.test(message) ? 'instalment' : 'other';
          this.dataset.state = 'ready';
        } else if (Date.now() - startedAt > RENDER_TIMEOUT) {
          clearInterval(this.renderTimer);
          this.unavailable();
        }
      }, 120);
    }
    unavailable() {
      // Keine Fehlermeldung im Kaufbereich: ohne offizielle Rate keine Zeile.
      this.dataset.state = 'unavailable';
      this.hidden = true;
    }
  }
  customElements.define('sf-klarna-financing', Financing);

  // Kaufbereiche weiter unten (Landingpages) sollen beim Hinscrollen schon
  // fertig sein: nach dem Seitenaufbau Klarna im Leerlauf vorladen.
  const warmup = () => document.querySelector('sf-klarna-financing:not([hidden])')?.start();
  const idle = () => (window.requestIdleCallback ? requestIdleCallback(warmup, { timeout: 2500 }) : setTimeout(warmup, 300));
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
})();
